import ErrorHandler from '@/exception/ErrorHandler';
import store from '@/store';
import axios from 'axios';
import { autoSpinner } from './spinner';
import { Utils } from './utils';
import { handle401Error, addPendingRequest, isHandling401 } from './authHandler';

// 영원히 resolve되지 않는 Promise (401/취소 시 후속 코드 실행 방지)
const PENDING_PROMISE = new Promise(() => {});

const getInstance = (serviceURL, testServer) => {
  let server = testServer ?? process.env.VUE_APP_URL_BASE_SERVER;

  const instance = axios.create({
    baseURL: server + serviceURL,
    headers: { 'Access-Control-Allow-Origin': '*' },
  });

  const authHeader = (req) => {
    const token = Utils.getSessionStorage('token');
    if (token) {
      req.headers.authorization = token;
    }
    return req.headers;
  };

  // 스피너 시작
  autoSpinner.start();

  // ------------ request ------------
  instance.interceptors.request.use(
    (req) => {
      // 요청 취소 토큰 설정
      const source = axios.CancelToken.source();
      req.cancelToken = source.token;
      addPendingRequest(source.cancel);

      req.headers = authHeader(req);
      return req;
    },
    (error) => {
      // 401 처리 중이면 에러 무시
      if (isHandling401()) {
        autoSpinner.stop();
        return PENDING_PROMISE;
      }
      ErrorHandler.request(error);
      autoSpinner.stop();
      return Promise.reject(error);
    }
  );

  // ------------ response ------------
  instance.interceptors.response.use(
    // ------- success -------
    ({ headers, data }) => {
      // 401 처리 중이면 응답 무시
      if (isHandling401()) {
        autoSpinner.stop();
        return PENDING_PROMISE;
      }

      if (data.statusCode === 200) {
        if (headers.authorization) {
          store.dispatch('authority/setToken', headers.authorization);
        }
        autoSpinner.stop();
        if (data.data.message) {
          return data.data.message;
        }
        return data.data?.result ?? data.data;
      }
      // ------- 401 in response body (토큰 만료) -------
      else if (data.statusCode === 401) {
        autoSpinner.stop();
        handle401Error(data.message || data.data?.message);
        return PENDING_PROMISE;
      }
      // ------- normal error -------
      else {
        ErrorHandler.response(data);
        autoSpinner.stop();
        const error = new Error(
          data.message || `Request failed with status ${data.statusCode}`
        );
        error.data = data;
        error.statusCode = data.statusCode;
        return Promise.reject(error);
      }
    },
    // ------- network error -------
    (error) => {
      // 요청이 취소된 경우 (401로 인한 취소)
      if (axios.isCancel(error)) {
        autoSpinner.stop();
        return PENDING_PROMISE;
      }

      // 이미 401 처리 중이면 후속 코드 실행 불필요
      if (isHandling401()) {
        autoSpinner.stop();
        return PENDING_PROMISE;
      }

      const status = error.response?.status;

      // 401 Unauthorized - 토큰 만료, 재로그인 필요
      if (status === 401) {
        autoSpinner.stop();
        handle401Error(error.response?.data?.message);
        return PENDING_PROMISE;
      }

      // 403 Forbidden - 권한 없음
      if (status === 403) {
        ErrorHandler.response(error.response?.data);
        autoSpinner.stop();
        return Promise.reject(error);
      }

      // 기타 에러 (네트워크 에러 포함)
      ErrorHandler.network(error);
      autoSpinner.stop();
      return Promise.reject(error);
    }
  );

  return instance;
};

export default getInstance;
